import * as dateFns from 'date-fns';
import {
  Divider,
  Typography,
  TextField,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CurrencyField } from '../scenarios/CurrencyField';
import { PercentField } from '../scenarios/PercentField';

const interestTypes = ['fixed', 'variable'];

function EditLiabilityRow({ value: { liability: value }, onChange, onDelete }) {
  const end_date =
    value?.expected_payoff_date ??
    (value?.origination_date && value?.term
      ? dateFns.addYears(dateFns.parseISO(value?.origination_date), Number(value?.term))
      : undefined);

  const vm = {
    name: value?.name ?? value?.loan_name ?? value?.account?.name,
    outstanding_balance: value?.amount ?? value?.account?.current_balance,
    origination_principal_amount: value?.origination_principal_amount,
    origination_date: value?.origination_date ?? new Date(),
    term:
      value?.term ??
      (end_date &&
        Math.abs(
          dateFns.differenceInYears(
            dateFns.parseISO(end_date),
            dateFns.parseISO(value?.origination_date)
          )
        )),
    interest_rate_percentage: `${value?.interest_rate_percentage}%`,
    interest_type: value?.interest_type,
  };

  const canEdit = value?.provider === 'manual';

  const handleChange = (key, newValue) => {
    const updatedLiability = { ...value, [key]: newValue };
    onChange(updatedLiability);
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%' }}
      spacing={2}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: '100%' }}
      >
        <TextField
          sx={{ minWidth: '33%' }}
          placeholder="Liability name"
          label="Liability name"
          size="small"
          value={vm?.name}
          onChange={(e) => handleChange('name', e.target.value)}
        />

        <CurrencyField
          sx={{ minWidth: '33%' }}
          label="Principal"
          size="small"
          value={vm?.origination_principal_amount}
          onChange={(e) => handleChange('origination_principal_amount', e.target.value)}
          // disabled={!canEdit}
        />

        <CurrencyField
          sx={{ minWidth: '33%' }}
          label="Amount"
          size="small"
          value={vm?.outstanding_balance}
          onChange={(e) => handleChange('amount', e.target.value)}
          disabled={canEdit}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: '100%' }}
      >
        <DatePicker
          sx={{ minWidth: '33%' }}
          label="Effective Date"
          value={new Date(vm?.origination_date)}
          onChange={(newValue) => handleChange('origination_date', newValue)}
          renderInput={(params) => <TextField size="small" {...params} />}
          slotProps={{ field: { size: 'small' } }}
        />

        <TextField
          sx={{ minWidth: '33%' }}
          label="Term (years)"
          size="small"
          type="number"
          value={vm?.term}
          onChange={(e) => handleChange('term', e.target.value)}
          // disabled={!canEdit}
        />

        <PercentField
          sx={{ minWidth: '33%' }}
          placeholder={0}
          label="Interest Rate (%)"
          size="small"
          type="number"
          value={vm?.interest_rate_percentage}
          onChange={(e) => handleChange('interest_rate_percentage', e.target.value)}
          // disabled={!canEdit}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ width: '100%' }}
      >
        <FormControl fullWidth size="small">
          <InputLabel>Interest Type</InputLabel>
          <Select
            label="Interest Type"
            value={vm?.interest_type || ''}
            onChange={(e) => handleChange('interest_type', e.target.value)}
          >
            {interestTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Stack>
  );
}

const Row = ({ children }) => (
  <Stack
    direction="row"
    alignItems="flex-start"
    justifyContent="space-between"
    spacing={1}
    sx={{ width: '100%' }}
  >
    {children}
  </Stack>
);

export function LiabilityRow({ value, onChange, onDelete }) {
  const { liability } = value;

  const vm = liabilityViewModel(liability);

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      sx={{ width: '100%' }}
      spacing={1}
    >
      <Row>
        <Typography fontWeight="bold">Liability name:</Typography>
        <Typography>{vm?.name}</Typography>
      </Row>

      <Row>
        <Typography fontWeight="bold">Principal:</Typography>
        <Typography>{vm?.origination_principal_amount}</Typography>
      </Row>

      <Row>
        <Typography fontWeight="bold">Amount:</Typography>
        <Typography>{vm?.outstanding_balance}</Typography>
      </Row>

      <Row>
        <Typography fontWeight="bold">Effective Date:</Typography>
        <Typography>{vm?.origination_date}</Typography>
      </Row>

      <Row>
        <Typography fontWeight="bold">Term (years):</Typography>
        <Typography>{vm?.term}</Typography>
      </Row>

      <Row>
        <Typography fontWeight="bold">Interest Rate (%):</Typography>
        <Typography>{vm?.interest_rate_percentage}</Typography>
      </Row>
      {/* <Row>
        <Typography fontWeight="bold">Interest Type:</Typography>
        <Typography>{vm?.interest_type}</Typography>
      </Row> */}
      <Divider />
    </Stack>
  );
}

export default EditLiabilityRow;

// formats the liability nicely
const { isValid, parseISO, differenceInYears, format, addYears } = dateFns;

export const liabilityViewModel = (liability) => {
  const originationDate = isValid(parseISO(liability?.origination_date))
    ? parseISO(liability.origination_date)
    : null;

  const expectedPayoffDate = isValid(parseISO(liability?.expected_payoff_date))
    ? parseISO(liability.expected_payoff_date)
    : liability?.term && originationDate
    ? addYears(originationDate, Number(liability?.term))
    : null;

  const result = {
    id: liability?.id,
    provider: liability?.provider,
    name: liability?.name ?? liability?.loan_name ?? liability?.account?.name,
    outstanding_balance:
      liability?.amount ?? liability?.account?.current_balance ?? liability?.outstanding_balance,
    origination_principal_amount: liability?.origination_principal_amount,
    origination_date: liability?.origination_date,
    term:
      liability?.term ??
      (expectedPayoffDate && originationDate
        ? Math.abs(differenceInYears(expectedPayoffDate, originationDate))
        : undefined),
    expected_payoff_date: expectedPayoffDate ? format(expectedPayoffDate, 'yyyy-MM-dd') : undefined,
    interest_rate_percentage: liability?.interest_rate_percentage,
    interest_type: liability?.interest_type,
    ...liabilityTypesForLiability(liability),
  };

  return result;
};

const liabilityTypesForLiability = (liability) => {
  const type = liability?.type ?? liability?.account?.type;
  const subtype = liability?.subtype ?? liability?.account?.subtype;

  return { type, subtype };
};
